/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { CheckOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons'
import { Col, Row, Space, Input } from 'antd'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { ListReq, Data } from 'Store/merchants/types'
import { selectList, selectListEvent } from 'Store/merchants/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { formatBusinessType, formatDateTime, getStatus } from 'helpers/format'
import { MerchantStatusTag, StatusTag } from 'Components/utilities'
import { menuMerchantStatus, MerchantStatus } from 'helpers/constanst'
import { TableListPage } from 'Components/common/Table'
import { ActionButton, PrimaryButton } from 'Components/button'
import HomePageLayout from 'Pages/Layout/PageLayout'
import Detail from './detail'
import { Pagination } from 'Components/Pagination'
import { merchantsListAPI } from 'Store/merchants/api'
import Label from 'Components/text/Label'
import { MyTheme } from 'Components/common/Theme'
import { SelectItem } from 'Components/form'
import { themeConfig } from 'config'

type Props = {} & ReduxProps

const Merchants: React.FunctionComponent<Props> = props => {
  const { currentUser, listEvent, dataList } = props
  const dispatch = useDispatch()
  const [drawer, setDrawer] = useState(false)
  const [selectedID, setSelectedID] = useState(0)
  const [pageMode, setPageMode] = useState('edit' as 'approve' | 'edit')

  const [filter, setFilter] = useState({
    keyword: '',
    status: '',
  })
  const [pagination, setPagination] = useState({
    Page: 1,
    RowPerPage: 20,
  })
  const meta = dataList.meta
  const datas = dataList.data

  const onGetDataList = (): void => {
    const req: ListReq = {
      keyword: filter.keyword?.trim() || '',
      page: pagination.Page,
      per_page: pagination.RowPerPage,
    }
    if (filter.status === MerchantStatus.WAITING) {
      req.is_verified = false
      req.is_rejected = false
    }
    if (filter.status === MerchantStatus.APPROVE) {
      req.is_verified = true
    }
    if (filter.status === MerchantStatus.REJECT) {
      req.is_rejected = true
    }

    dispatch(merchantsListAPI(req, currentUser.accessToken))
  }

  useEffect(() => {
    if (currentUser?.accessToken) {
      onGetDataList()
    }
  }, [pagination])

  useEffect(() => {
    if (pagination.Page === 1) return
    pagination.Page = 1
    pagination.RowPerPage = 20
    setPagination({ ...pagination })
  }, [])

  const onFilter = (key: string, val: string) => {
    setFilter({
      ...filter,
      [key]: val,
    })
  }

  const onOpenDrawer = () => {
    setDrawer(true)
  }

  const onCloseDrawer = () => {
    setDrawer(false)
  }

  const onClickEdit = (id: number) => {
    setPageMode('edit')
    setSelectedID(id)
    onOpenDrawer()
  }

  const onClickApprove = (id: number) => {
    setPageMode('approve')
    setSelectedID(id)
    onOpenDrawer()
  }

  const onClickSearch = () => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }

  const onSuccess = () => {
    onCloseDrawer()
    onGetDataList()
  }

  const renderStatus = (status: string, record: Data) => (
    <MerchantStatusTag rejected={record?.rejected} verified={record?.verified} />
  )

  const renderMsProductStatus = (isMaster: boolean) => (
    <StatusTag isPass={isMaster} text={getStatus(isMaster)} />
  )

  const renderActionButton = (text: string, record: Data) => (
    <Space size="middle">
      <ActionButton
        text=""
        icon={<EditOutlined />}
        label="แก้ไขข้อมูลร้านค้า"
        action="edit"
        onClick={() => onClickEdit(record.id)}
      />
      {!record?.verified && !record?.rejected && (
        <ActionButton
          text=""
          icon={<CheckOutlined />}
          label="อนุมัติร้านค้า"
          action="success"
          onClick={() => onClickApprove(record.id)}
        />
      )}
    </Space>
  )

  const renderOwner = (text: string, record: Data) => (
    <div>
      <span>
        {record.owner_firstname} {record.owner_lastname}
      </span>{' '}
      <br />
      <PhoneLabel>เบอร์ติดต่อร้านค้า : {record.phone}</PhoneLabel>
      {/* <br />
      <PhoneLabel>วันที่สมัคร : {formatDateTime(record.created_at)}</PhoneLabel> */}
    </div>
  )

  const getMerchantName = (text: string, record: Data) => {
    let color = 'orange'
    if (record.verified && !record.rejected) {
      color = 'green'
    }
    if (record.rejected) {
      color = 'red'
    }
    return (
      <Hilight className={color}>
        <MerchantName>{text}</MerchantName>
      </Hilight>
    )
  }

  const columns = [
    {
      title: 'ชื่อร้านค้า',
      dataIndex: 'name',
      key: 'name',
      render: getMerchantName,
    },
    {
      title: 'เจ้าของร้าน',
      dataIndex: 'owner_firstname',
      key: 'owner_firstname',
      render: renderOwner,
    },
    {
      title: 'ประเภทร้านค้า',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => formatBusinessType(text),
    },
    {
      title: 'วัน/เวลาที่สมัคร',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'สินค้าตัวกลาง',
      dataIndex: 'master_product',
      key: 'master_product',
      render: renderMsProductStatus,
    },
    {
      title: 'สถานะ',
      key: 'verified',
      dataIndex: 'status',
      render: renderStatus,
    },
    {
      title: 'แก้ไข/อนุมัติ',
      key: 'action',
      render: renderActionButton,
      //fixed: 'right',
      width: '100px',
    },
  ]

  const DetailDrawer = (
    <Detail
      mode={pageMode}
      id={selectedID}
      visible={drawer}
      onCloseDrawer={onCloseDrawer}
      onSuccess={onSuccess}
    />
  )

  const FilterContent = (
    <FilterWrapper>
      <Row justify="start" align="bottom" gutter={16}>
        <MyCol xs={24} md={6}>
          <SearchInput
            placeholder="ค้นหาชื่อร้าน,ชื่อเจ้าของร้านค้า"
            prefix={<SearchOutlined />}
            allowClear
            value={filter.keyword}
            onChange={(evt: any): void => onFilter('keyword', evt.target.value)}
          />
        </MyCol>
        <MyCol xs={24} md={6}>
          <SelectItem
            name={'discount_type'}
            label={''}
            isRequired={true}
            value={filter.status}
            menu={menuMerchantStatus}
            placeHolder={'สถานะร้านค้า'}
            onChange={(val: any): void => onFilter('status', val)}
          />
        </MyCol>
        <MyCol>
          <PrimaryButton text="ค้นหา" btnType="Cancel" onClick={onClickSearch} />
        </MyCol>
      </Row>
    </FilterWrapper>
  )

  const PaginationContent = (
    <Pagination
      currentPage={pagination.Page}
      totalRows={meta?.totalCount || 0}
      perPage={pagination.RowPerPage}
      onClickPage={(page: number) => {
        pagination.Page = page
        setPagination({ ...pagination })
      }}
      onChangeNumber={(number: number) => {
        pagination.RowPerPage = number
        setPagination({ ...pagination })
      }}
      changePerPage={true}
    />
  )

  return (
    <>
      <HomePageLayout
        selected="merchants"
        title="พันธมิตรทางธุรกิจ"
        label={`รายการพันธมิตรทางธุรกิจ ที่เป็นคู่คิดและพันธมิตรทางธุรกิจของ ${themeConfig.title.appName}`}
      >
        {FilterContent}
        <TableListPage
          columns={columns}
          datas={datas}
          isLoading={listEvent.IsLoading}
          paginationContent={PaginationContent}
        />
        {DetailDrawer}
      </HomePageLayout>
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    dataList: selectList(state),
    listEvent: selectListEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

const FilterWrapper = styled.div`
  padding: 10px 0 5px 0;
`

const MyCol = styled(Col)`
  margin-top: 10px;
`

const SearchInput = styled(Input)`
  border-radius: 6px;
  height: 45px;
`

const PhoneLabel = styled(Label)`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const Hilight = styled.div`
  height: 100%;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  border-left: 5px solid;
  border-radius: 5px;
  &.red {
    border-left-color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
  }
  &.orange {
    border-left-color: ${(props: { theme: MyTheme }): string => props.theme.colors.warning};
  }
  &.green {
    border-left-color: ${(props: { theme: MyTheme }): string => props.theme.colors.success};
  }
`

const MerchantName = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding-left: 20px;
`

export default connect(mapStateToProps)(Merchants)
